import { inject, Injectable, Injector, signal, WritableSignal } from '@angular/core'
import { HttpService, LoggerService, ServiceType } from '@danangelus/ngx-app-core'
import { CookieService } from 'ngx-cookie-service'
import { GatewayError, SessionError } from '../../models/gateway.model'
import { User } from '../../models/user.model'
import { AuthenticationService } from './authentication.service'
import { isServerError, withRetry } from '../utils/retry.utils'

const log = LoggerService.get('SessionService')

/**
 * Handles session checking
 *
 * @author Dan Bennett
 */
@Injectable({
    providedIn: 'root'
})
export class SessionService {

    user: WritableSignal<User | null> = signal(null)
    loggedOut: WritableSignal<boolean> = signal(false)

    // @ts-expect-error lazy loaded
    private http: HttpService
    private injector = inject(Injector)
    private cookieService = inject(CookieService)

    constructor() {
        AuthenticationService.authenticated.subscribe((user: User | null) => this.user.set(user))
    }

    async isSessionActive(): Promise<boolean | SessionError> {
        if (!this.http) this.http = this.injector.get(HttpService)
        log.trace('isSessionActive', 'Checking if current user has an existing session...')
        try {
            if (this.user()?.ssoTicketId != null || this.cookieService.get('SSOTicketId') != null) {
                const sessionActive = await withRetry(
                    () => this.http.get(ServiceType.SSO, 'check-session-active', null, null, false, true),
                    { maxRetries: 3, shouldRetry: isServerError, label: 'isSessionActive' }
                )

                // If still a server error after retries, don't assume session is dead
                if (isServerError(sessionActive)) {
                    return false
                }

                return sessionActive.status == 200 || sessionActive == true
            }
        } catch (e) {
            log.error(`Error checking session, assuming no active session: ${e}`)
        }
        return false
    }

    async loadSession(): Promise<User | GatewayError | null> {
        if (!this.http) this.http = this.injector.get(HttpService)
        log.trace('loadSession', 'Loading session...')

        try {
            if (this.user()?.ssoTicketId != null || this.cookieService.get('SSOTicketId') != null) {
                const response = await withRetry(
                    () => this.http.get(ServiceType.SSO, 'load-session', null, null, false, true),
                    { maxRetries: 3, shouldRetry: isServerError, label: 'loadSession' }
                )

                if (isServerError(response)) {
                    log.error('loadSession failed after retries due to server error.')
                    return null
                }

                if (Object.prototype.hasOwnProperty.call(response, 'error')) {
                    return new GatewayError(response)
                } else {
                    this.user.set(new User(response))
                    AuthenticationService.authenticated.next(new User(response))
                    return this.user()
                }
            }
        } catch (e) {
            log.error(`Error loading session ${e}`)
        }
        return null
    }

    async invalidateSession(username: string | null = null, ticketId: string | null = null): Promise<boolean | GatewayError> {
        if (!this.http) this.http = this.injector.get(HttpService)
        log.trace('invalidateSession', 'Invalidating session...')
        const response: any = await withRetry(
            () => this.http.get(ServiceType.SSO, 'invalidate-session', null, { username: username || '', SSOTicketId: ticketId }, false, true),
            { maxRetries: 3, shouldRetry: isServerError, label: 'invalidateSession' }
        )
        if (!username && !ticketId) {
            // Invalidating current session so delete the cookie as part of tidy up
            this.cookieService.delete('SSOTicketId')
        }
        return response
    }

    async getUser(): Promise<User | null> {
        if (this.user() == null) {
            await this.loadSession()
        }
        return this.user()
    }

    userVerified(user: User): void {
        this.user.set(user)
        AuthenticationService.authenticated.next(user)
    }

    passwordReset(): void {
        const user = this.user()!
        user.passwordExpired = false
        this.user.set(user)
        AuthenticationService.authenticated.next(user)
    }
}
